import { AlertTriangle, Clock } from 'lucide-react';

import { Badge } from '@/ui/base/badge';
import { STATUS_TONE } from '@/nucleo/statusBadgeTokens';
import { cn } from '@/nucleo/utilitarios';

import type { StatusDoRecurso } from './contrato';

/** Só faz sentido contar prazo enquanto o recurso está "vivo" — depois de decidido, o prazo não importa mais. */
const STATUS_COM_PRAZO_VIVO: StatusDoRecurso = 'em_analise';

function diasAte(dataIso: string): number {
  const hoje = new Date();
  const hojeSemHora = new Date(hoje.getFullYear(), hoje.getMonth(), hoje.getDate());
  const alvo = new Date(`${dataIso}T00:00:00`);
  const umDiaEmMs = 24 * 60 * 60 * 1000;
  return Math.round((alvo.getTime() - hojeSemHora.getTime()) / umDiaEmMs);
}

interface PrazoRespostaCountdownProps {
  prazoResposta: string;
  status: StatusDoRecurso;
}

/**
 * Contagem regressiva do prazo legal de resposta de um recurso — verde
 * quando falta mais de uma semana, amarelo quando o prazo está iminente,
 * vermelho quando já venceu. Só aparece com o recurso em análise: depois de
 * deferido/indeferido/em recurso superior, o prazo de resposta já não é
 * informação acionável.
 */
export function PrazoRespostaCountdown({ prazoResposta, status }: PrazoRespostaCountdownProps) {
  if (status !== STATUS_COM_PRAZO_VIVO) {
    return null;
  }

  const dias = diasAte(prazoResposta);

  if (dias > 7) {
    return (
      <Badge variant="outline" className={cn('gap-1 font-medium', STATUS_TONE.success)}>
        <Clock className="h-3 w-3" aria-hidden="true" />
        {`D-${dias}`}
      </Badge>
    );
  }

  if (dias >= 0) {
    return (
      <Badge variant="outline" className={cn('gap-1 font-medium', STATUS_TONE.warning)}>
        <Clock className="h-3 w-3" aria-hidden="true" />
        {dias === 0 ? 'Vence hoje' : `D-${dias}`}
      </Badge>
    );
  }

  return (
    <Badge variant="outline" className={cn('gap-1 font-medium', STATUS_TONE.destructive)}>
      <AlertTriangle className="h-3 w-3" aria-hidden="true" />
      {`${Math.abs(dias)}d em atraso`}
    </Badge>
  );
}
